// React components
import { useState, useEffect, useContext } from "react";
import partCategoryService from "services/part-category-service";

// MUI and Material Dashboard Components
import Checkbox from "@mui/material/Checkbox";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

export default function AttributeDialog({
    handleClose,
    open,
    id,
    attNum,
    attributeData,
    existingAttributes,
    editMode = false,
}) {
    const [attribute, setAttribute] = useState(attributeData || {});
    const [enableMultiVals, setEnableMultiVals] = useState(false);
    const [disableMultiVals, setDisableMultiVals] = useState(false);
    const [disabledFields, setDisabledFields] = useState({
        options: false,
        description: false,
        units: false
    });
    const [error, setError] = useState({
        attribute: false,
        options: false,
        description: false,
        units: false
    })

    // Initializes the initial states depending on the fetched attribute data (from props)
    useEffect(() => {
        setAttribute(attributeData);
        setEnableMultiVals(attributeData?.enableMultiVals || false);
    }, [attributeData]);

    // Handles the changes of the dialog's fields
    const attChangeHandler = (e) => {
        setAttribute({
            ...attribute,
            [e.target.name]: e.target.value
        })
    }
    useEffect(() => {
        setDisableMultiVals(!attribute.options);

        setDisabledFields({
            options: !!attribute.units,
            description: !!attribute.units,
            units: !!(attribute.options || attribute.description)
        });

        setEnableMultiVals(enableMultiVals && !!(attribute.options && attribute.description));

    }, [attribute]);

    // Function to determine the field type based on the attribute's properties.
    // This is used to determine how the attribute will be rendered in the preview.
    const findFieldType = (attribute) => {
        let newFieldType = "";

        if (attribute.options) {
            newFieldType = "select";
        } else if (attribute.attribute && attribute.units) {
            newFieldType = "text-with-units";
        } else {
            newFieldType = "text";
        }

        return newFieldType;
    }

    // Validator function to check for missing or invalid fields
    const checkErrors = () => {

        // Checks if the attribute name is provided
        // Checks if the description is provided when options are given and vice versa
        const newErrors = {
            attribute: !attribute.attribute ? "This field is required" : false,
            description: attribute.options && !attribute.description ? "Description is required when options are provided" : false,
            options: !attribute.options && attribute.description ? "Options are required when description is provided" : false,
            units: false
        };

        // Check if options and descriptions have matching lengths when split
        if (attribute.options && attribute.description) {
            const optionsArr = attribute.options.split(';').map(o => o.trim()).filter(o => o);
            const descArr = attribute.description.split(';').map(d => d.trim()).filter(d => d);

            if (optionsArr.length !== descArr.length) {
                newErrors.options = "Number of options must match number of descriptions";
                newErrors.description = "Number of descriptions must match number of options";
            }
        }

        if (attribute.attribute.toLowerCase() === "type") {
            newErrors.attribute = "This attribute name is reserved and cannot be used";
        } else {
            // Check if the attribute label already exists (case-insensitive)
            const existingLabels = Object.values(existingAttributes)
                .filter((_, index) => ('0' in existingAttributes ? index : index + 1) !== attNum) // Remove current attribute
                .map(attr => attr.attribute?.toLowerCase());
            if (existingLabels.includes(attribute.attribute?.toLowerCase())) {
                newErrors.attribute = "This attribute name already exists";
            }
        }

        setError(newErrors);
        return Object.values(newErrors).every(err => err === false);
    }

    const handleMultiValsMode = () => {
        let multivals = enableMultiVals;
        if (enableMultiVals) {
            multivals = false;
        } else {// Only allow multi-values mode when both options and description are filled
            if (attribute.options && attribute.description) {
                multivals = true;
                setError({
                    attribute: false,
                    options: false,
                    description: false,
                    units: false
                });
            } else {
                setError({
                    ...error,
                    options: !attribute.options ? "Options are required for multi-values mode" : false,
                    description: !attribute.description ? "Description is required for multi-values mode" : false
                });
            }
        }

        setEnableMultiVals(multivals);
        attChangeHandler({
            target: {
                name: 'enableMultiVals',
                value: multivals
            }
        });
    }

    // Exit on cancel/clicking outside the dialog
    const handleCloseDialog = () => {
        setError({
            attribute: false,
            options: false,
            description: false,
            units: false
        });
        handleClose();
    }

    // Exit on save
    const clickSaveHandler = async () => {
        if (!checkErrors()) return;
        setAttribute({});

        const toSubmitAtts = {
            ...attribute,
            fieldType: findFieldType(attribute)
        };

        if (!editMode) {
            handleClose(toSubmitAtts, attNum);
        } else {
            const payload = {
                data: {
                    type: "partCategory",
                    id: id,
                    attributes: {
                        key: attNum,
                        value: toSubmitAtts
                    }
                }
            }
            // Call the API to update the attribute
            await partCategoryService.editAttribute(payload, id);
            handleClose(toSubmitAtts, attNum);
        }
    }
    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >

            <DialogContent>
                <MDBox margin={2}>

                    {/* Title and instructions */}
                    <MDBox mb={2} mt={2}>
                        <MDTypography variant="h5" fontWeight="bold" mt={1}>
                            Define the Attribute No.{attNum} :
                        </MDTypography>
                        <DialogContentText>
                            Please input the attribute's name*, options, description and units.
                            When entering multiple values in the options, description or units field please seperate the values with a semi-colon (;).
                        </DialogContentText>
                    </MDBox>

                    {/* Main content */}
                    <MDBox>

                        {/* Attribute Name */}
                        <Grid container spacing={3} mb={1}>
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    key={`attribute`}
                                    label={`Attribute Name`}
                                    value={attribute[`attribute`] || ''}
                                    onChange={attChangeHandler}
                                    name={`attribute`}
                                    fullWidth
                                    error={!!error.attribute}
                                    inputProps={{
                                        autoComplete: "description",
                                        form: {
                                            autoComplete: "off",
                                        },
                                    }}
                                />
                                {error.attribute && (
                                    <MDTypography variant="caption" color="error">
                                        {error.attribute}
                                    </MDTypography>
                                )}
                            </Grid>

                            {/* Attribute Options */}
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    key={`options`}
                                    label={`Attribute Options`}
                                    value={attribute[`options`] || ''}
                                    onChange={attChangeHandler}
                                    name={`options`}
                                    fullWidth
                                    error={!!error.options}
                                    disabled={disabledFields.options}
                                    inputProps={{
                                        autoComplete: "examples",
                                        form: {
                                            autoComplete: "off",
                                        },
                                    }}
                                />
                                {error.options && (
                                    <MDTypography variant="caption" color="error">
                                        {error.options}
                                    </MDTypography>
                                )}
                            </Grid>

                        </Grid>
                        <Grid container spacing={3}>

                            {/* Attribute's option description */}
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    key={`description`}
                                    label={`Attribute Options Description`}
                                    value={attribute[`description`] || ''}
                                    onChange={attChangeHandler}
                                    name={`description`}
                                    fullWidth
                                    error={!!error.description}
                                    disabled={disabledFields.description}
                                    inputProps={{
                                        autoComplete: "description",
                                        form: {
                                            autoComplete: "off",
                                        },
                                    }}
                                />
                                {error.description && (
                                    <MDTypography variant="caption" color="error">
                                        {error.description}
                                    </MDTypography>
                                )}
                            </Grid>

                            {/* Attribute Units */}
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    key={`units`}
                                    label={`Attribute Units`}
                                    value={attribute[`units`] || ''}
                                    onChange={attChangeHandler}
                                    name={`units`}
                                    fullWidth
                                    error={!!error.units}
                                    disabled={disabledFields.units}
                                    inputProps={{
                                        autoComplete: "description",
                                        form: {
                                            autoComplete: "off",
                                        },
                                    }}
                                />
                                {error.units && (
                                    <MDTypography variant="caption" color="error">
                                        {error.units}
                                    </MDTypography>
                                )}
                            </Grid>
                        </Grid>

                        <MDBox mt={1}>
                            <MDInput
                                key={'hintText'}
                                label={`Hint Text (Optional)`}
                                value={attribute[`hintText`] || ''}
                                onChange={attChangeHandler}
                                name={`hintText`}
                                fullWidth
                                error={!!error.hintText}
                                disabled={disabledFields.hintText}
                                inputProps={{
                                    autoComplete: "hintText",
                                    form: {
                                        autoComplete: "off",
                                    },
                                }}
                            />
                        </MDBox>

                    </MDBox>

                </MDBox>
            </DialogContent>

            <DialogActions>
                <MDBox display="flex" justifyContent="space-between" width="100%">
                    <MDBox>
                        <MDButton
                            color={enableMultiVals ? 'light' : 'dark'}
                            variant="gradient"
                            size="small"
                            onClick={handleMultiValsMode}
                        >
                            {enableMultiVals ? 'Disable multi values mode' : 'Enable multi values mode'}
                        </MDButton>
                    </MDBox>
                    <MDBox display="flex">
                        <MDBox mr={1}><MDButton color="dark" variant="gradient" size="small" onClick={handleCloseDialog}>Cancel</MDButton></MDBox>
                        <MDBox><MDButton color="dark" variant="gradient" size="small" onClick={clickSaveHandler}>Save</MDButton></MDBox>
                    </MDBox>
                </MDBox>


            </DialogActions>

        </Dialog>
    )
}
